import { createBrowserRouter, Navigate, useLocation } from "react-router-dom";
import { Loader } from "@themes/pollinator/ui/Loader";
import { usePlatformStore } from "./store";
import { makeRoute } from "@core/utils/makeRoute";
import { useSystemVariable } from "./hooks/useSystemVariable";
import NotFoundPage from "@themes/pollinator/pages/NotFoundPage";
import { NewsAddressGuardContainer } from "./features/news";
import ErrorPage from "@themes/pollinator/pages/ErrorPage";

// Wrapper component to protect routes that require authentication
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const user = usePlatformStore((s) => s.user);
  const isHydrated = usePlatformStore((s) => s.isHydrated);
  const location = useLocation();

  // Wait for store hydration from localStorage
  if (!isHydrated) {
    return <Loader isFullPage />;
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to={`/login?redirect=${location.pathname}`} replace />;
  }

  return <>{children}</>;
}


// Wrapper component to redirect away from video routes when video is disabled.
// Reads the system variable directly (not from the store) to avoid a race
// condition where the store default (true) redirects before PlatformApp's
// useEffect can set the real value.
function VideoGuard({ children }: { children: React.ReactNode }) {
  const { data: isVideoDisabledVar, isLoading } = useSystemVariable("is_video_disabled");

  // While the system variable is loading, render children optimistically
  if (isLoading || isVideoDisabledVar?.value === undefined) {
    return <>{children}</>;
  }

  if (isVideoDisabledVar.value === "true") {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Bind makeRoute with the app's error page
const route = (
  path: string,
  element: React.ReactNode,
  layout: React.ReactNode
) => makeRoute(path, element, layout, <ErrorPage />);

// Helper for routes that require authentication
const protectedRoute = (
  path: string,
  element: React.ReactNode,
  layout: React.ReactNode
) =>
  makeRoute(
    path,
    <ProtectedRoute>{element}</ProtectedRoute>,
    layout,
    <ErrorPage />
  );

// Pages - imported from apps/platform/pages/
import DashboardPage from "./pages/DashboardPage";
import DashboardProgramPage from "./pages/DashboardProgramPage";
import DashboardSettingsPage from "./pages/DashboardSettingsPage";
import DashboardSupportHomePage from "./pages/DashboardSupportHomePage";
import DashboardSupportArticlePage from "./pages/DashboardSupportArticlePage";
import LoginPage from "./pages/LoginPage";
import LogoutPage from "./pages/LogoutPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import InvitationJoinPage from "./pages/InvitationJoinPage";
import InvitationFitQuizPage from "./pages/InvitationFitQuizPage";
import InvitationSignupPage from "./pages/InvitationSignupPage";
import InvitationsPage from "./pages/InvitationsPage";
import MarketingHome from "./pages/MarketingHome";
import ContactPage from "./pages/ContactPage";
import SupportRedirectPage from "./pages/SupportRedirectPage";
import ApplyPage from "./pages/ApplyPage";
import UnsubscribePage from "./pages/UnsubscribePage";
import NewsPostPage, { BlogPostRedirect } from "./pages/NewsPostPage";
import ProgramPage from "./pages/ProgramPage";
import ResourcesPage from "./pages/ResourcesPage";
import ProfilePage from "./pages/ProfilePage";
import ParticipantOnboardingPage from "./pages/ParticipantOnboardingPage";
import OrgAdminOnboardingPage from "./pages/OrgAdminOnboardingPage";
import OrganizationAdminPage from "./pages/OrganizationAdminPage";

// Video pages
import VideoJoinPage from "./pages/VideoJoinPage";
import VideoMeetingPage from "./pages/VideoMeetingPage";
import VideoEndedPage from "./pages/VideoEndedPage";

// Layouts
import DashboardLayout from "./layouts/DashboardLayout";
import MarketingLayout from "./layouts/MarketingLayout";
import InvitationLayout from "./layouts/InvitationLayout";
import OnboardingLayout from "./layouts/OnboardingLayout";
import VideoLayout from "./layouts/VideoLayout";
import VideoEndLayout from "./layouts/VideoEndLayout";
import StaticPageLayout from "./layouts/StaticPageLayout";
import SupportLayout from "./layouts/SupportLayout";
import IntakeLayout from "./layouts/IntakeLayout";
import AuthLayout from "./layouts/AuthLayout";

// Static page container
import { StaticPageContainer } from "./features/staticPage";

// Onboarding gate
import { OnboardingGate } from "./features/onboarding";

// Enrollment-closed gate
import { EnrollmentClosedGate } from "./features/programPhases";

// Invitation-closed gate (marketing_site_mode === "closed")
import { InvitationClosedGate } from "./features/marketing";

// Login-closed gate (is_login_disabled)
import { LoginClosedGate } from "./features/marketing";

/** The homepage's sections, each reachable at `/<id>` as well as `/#<id>`. */
const HOME_SECTIONS = [
  { id: "program", title: "Program" },
  { id: "participants", title: "Participants" },
  { id: "organizations", title: "Organizations" },
  { id: "network", title: "Network" },
  { id: "fund", title: "Fund" },
  { id: "research", title: "Research" },
  { id: "team", title: "Team" },
  { id: "news", title: "News" },
  { id: "vision", title: "Vision" },
  { id: "donate", title: "Donate" },
];

export const router = createBrowserRouter([
  // Marketing
  route("/", <MarketingHome />, <MarketingLayout title="Home" />),
  route(
    "/programs",
    <Navigate to="/#program" replace />,
    <MarketingLayout title="Programs" />
  ),
  route(
    "/how-it-works",
    // The homepage has no "how it works" section; the program section is
    // where it explains how Pollinator works.
    <Navigate to="/#program" replace />,
    <MarketingLayout title="How It Works" />
  ),
  // Each section of the homepage has an address of its own as well as its
  // anchor, so "/fund" can be said aloud or put in print and lands on the
  // section the way "/#fund" does.
  ...HOME_SECTIONS.map(({ id, title }) =>
    route(
      `/${id}`,
      // News is offered only once something is published, so until then its
      // address answers as any other missing page does.
      id === "news" ? (
        <NewsAddressGuardContainer />
      ) : (
        <Navigate to={`/#${id}`} replace />
      ),
      <MarketingLayout title={title} />
    )
  ),

  // Invitations
  route(
    "/invitation/:shortcode",
    // Accepting any invitation, an organization admin's included, ends in
    // signing in, so all of them close with login.
    <LoginClosedGate><InvitationClosedGate><MarketingHome /></InvitationClosedGate></LoginClosedGate>,
    <MarketingLayout title="You're Invited" />
  ),
  route(
    "/invitation/:shortcode/join",
    <LoginClosedGate><InvitationClosedGate><InvitationJoinPage /></InvitationClosedGate></LoginClosedGate>,
    <InvitationLayout title="Join Program" />
  ),
  route(
    "/invitation/:shortcode/fit",
    <LoginClosedGate><InvitationClosedGate><InvitationFitQuizPage /></InvitationClosedGate></LoginClosedGate>,
    <InvitationLayout title="Fit Quiz" />
  ),
  route(
    "/invitation/:shortcode/signup",
    <LoginClosedGate><InvitationClosedGate><InvitationSignupPage /></InvitationClosedGate></LoginClosedGate>,
    <InvitationLayout title="Sign Up" />
  ),

  // Auth
  route("/login", <LoginClosedGate><LoginPage /></LoginClosedGate>, <AuthLayout title="Login" />),
  route("/logout", <LogoutPage />, <AuthLayout title="Logout" />),
  route(
    "/forgot-password",
    <LoginClosedGate><ForgotPasswordPage /></LoginClosedGate>,
    <AuthLayout title="Forgot Password" />
  ),
  route(
    "/reset-password",
    <LoginClosedGate><ResetPasswordPage /></LoginClosedGate>,
    <AuthLayout title="Reset Password" />
  ),

  // Dashboard
  protectedRoute(
    "/dashboard",
    <DashboardPage />,
    <DashboardLayout title="Dashboard" />
  ),
  protectedRoute(
    "/dashboard/:id",
    <OnboardingGate><DashboardProgramPage /></OnboardingGate>,
    <DashboardLayout title="Dashboard" />
  ),
  protectedRoute(
    "/account",
    <DashboardSettingsPage />,
    <DashboardLayout title="Your Account" />
  ),
  // Redirect old /notifications URL to dashboard
  protectedRoute(
    "/notifications",
    <Navigate to="/dashboard" replace />,
    <DashboardLayout title="Dashboard" />
  ),
  protectedRoute(
    "/program/:id/invitations",
    <InvitationsPage />,
    <DashboardLayout title="Invitations" />
  ),
  protectedRoute(
    "/program/:id",
    <EnrollmentClosedGate><ProgramPage /></EnrollmentClosedGate>,
    <DashboardLayout title="Program" />
  ),
  protectedRoute(
    "/program/:id/:tab",
    <EnrollmentClosedGate><ProgramPage /></EnrollmentClosedGate>,
    <DashboardLayout title="Program" />
  ),
  protectedRoute(
    "/program/:id/resources",
    <EnrollmentClosedGate><ResourcesPage /></EnrollmentClosedGate>,
    <DashboardLayout title="Resources" />
  ),
  protectedRoute(
    "/program/:id/profile",
    <EnrollmentClosedGate><ProfilePage /></EnrollmentClosedGate>,
    <DashboardLayout title="Profile" />
  ),
  protectedRoute(
    "/program/:id/profile/:tab",
    <EnrollmentClosedGate><ProfilePage /></EnrollmentClosedGate>,
    <DashboardLayout title="Profile" />
  ),
  // Participant onboarding
  protectedRoute(
    "/program/:id/onboarding/participant",
    <ParticipantOnboardingPage />,
    <OnboardingLayout title="Onboarding" />
  ),
  // Org admin onboarding
  protectedRoute(
    "/program/:id/organization/:orgId/onboarding",
    <OrgAdminOnboardingPage />,
    <OnboardingLayout title="Onboarding" />
  ),

  // Organization Admin
  protectedRoute(
    "/program/:programId/organization/:orgId/admin",
    <OnboardingGate><OrganizationAdminPage /></OnboardingGate>,
    <DashboardLayout title="Organization Admin" />
  ),
  protectedRoute(
    "/program/:programId/organization/:orgId/admin/:tab",
    <OnboardingGate><OrganizationAdminPage /></OnboardingGate>,
    <DashboardLayout title="Organization Admin" />
  ),

  // Support (program-scoped)
  protectedRoute(
    "/program/:id/support",
    <DashboardSupportHomePage />,
    <DashboardLayout title="Support" />
  ),
  protectedRoute(
    "/program/:id/support/:articleId",
    <DashboardSupportArticlePage />,
    <DashboardLayout title="Support" />
  ),
  // Support, from the footer, opens the dashboard's own Support, signing in
  // first if need be. Old article addresses still lead to the dashboard.
  protectedRoute(
    "/support",
    <SupportRedirectPage />,
    <SupportLayout title="Support" />
  ),
  route(
    "/support/:articleId",
    <Navigate to="/dashboard" replace />,
    <SupportLayout title="Support" />
  ),

  // Video
  protectedRoute(
    "/video/join/:meetingId",
    <VideoGuard><VideoJoinPage /></VideoGuard>,
    <VideoLayout isWithNavigation />
  ),
  protectedRoute(
    "/video/join",
    <VideoGuard><VideoJoinPage /></VideoGuard>,
    <VideoLayout isWithNavigation />
  ),
  route(
    "/video",
    <VideoGuard><Navigate to="/video/join" replace /></VideoGuard>,
    <VideoLayout isWithNavigation />
  ),
  protectedRoute(
    "/video/meeting/:meetingId",
    <VideoGuard><VideoMeetingPage /></VideoGuard>,
    <VideoLayout />
  ),
  protectedRoute(
    "/video/end/:meetingId",
    <VideoEndedPage />,
    <VideoEndLayout />
  ),

  // Not Found
  route("/not-found", <NotFoundPage />, <StaticPageLayout title="Not Found" />),

  // Static Pages
  // The team section of the homepage is the about page now; the old address
  // still lands people there.
  route("/about", <Navigate to="/team" replace />, <MarketingLayout title="Team" />),
  route("/contact", <ContactPage />, <IntakeLayout title="Contact" />),
  // The unsubscribe link in an announcement. Its token names the person, so
  // it works without signing in, and is never closed with login.
  route("/unsubscribe/:token", <UnsubscribePage />, <IntakeLayout title="Unsubscribe" />),
  // Every form has an address named for what it does, and the slug it serves
  // is said here rather than read off the path. Contact has a page of its own
  // above, and the newsletter is a card on the homepage.
  route("/join", <ApplyPage formSlug="join" />, <IntakeLayout title="Join" />),
  route(
    "/add-your-people",
    <ApplyPage formSlug="add_your_people" />,
    <IntakeLayout title="Add your people" />
  ),
  route(
    "/partner-with-us",
    <ApplyPage formSlug="partner_with_us" />,
    <IntakeLayout title="Partner with us" />
  ),
  // News is a section of the homepage, reached at /news with the others
  // above; only its posts have pages of their own.
  route("/news/:slug", <NewsPostPage />, <MarketingLayout title="News" />),
  // The section was the blog before it was News.
  // The section this names exists only once something is published, so
  // sending it straight to the anchor landed a reader silently at the top
  // of the homepage with nothing to tell them why. It now asks first, and
  // goes to the homepage rather than a missing page, since this is an old
  // address people may still hold.
  route("/blog", <NewsAddressGuardContainer whenNone="/" />, <MarketingLayout title="News" />),
  route("/blog/:slug", <BlogPostRedirect />, <MarketingLayout title="News" />),

  route(
    "/terms",
    <Navigate to="/terms-of-service" replace />,
    <StaticPageLayout title="Terms of Service" />
  ),
  route("/:slug", <StaticPageContainer />, <StaticPageLayout title="" />),
]);
